import { useCallback, useRef, useState } from 'react'

const VIEWPORT_MARGIN = 8
// Keeps the header on screen so the window can always be dragged back.
const MIN_VISIBLE_HEIGHT = 48
const KEYBOARD_STEP = 16
const INITIAL_OFFSET = { right: 120, top: 120 }

const KEY_DELTAS = {
  ArrowLeft: [-KEYBOARD_STEP, 0],
  ArrowRight: [KEYBOARD_STEP, 0],
  ArrowUp: [0, -KEYBOARD_STEP],
  ArrowDown: [0, KEYBOARD_STEP],
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max))

// The site navbar is fixed above the studio, so the window must stay below it.
const getMinTop = () => (
  (document.querySelector('.wayfinder')?.getBoundingClientRect().bottom ?? 0) + VIEWPORT_MARGIN
)

function useDraggable(windowRef, width) {
  const [position, setPosition] = useState(() => ({
    x: Math.max(VIEWPORT_MARGIN, window.innerWidth - width - INITIAL_OFFSET.right),
    y: Math.max(getMinTop(), INITIAL_OFFSET.top),
  }))
  const dragOffsetRef = useRef(null)

  const moveTo = useCallback((x, y) => {
    const windowWidth = windowRef.current?.offsetWidth ?? width
    setPosition({
      x: clamp(x, VIEWPORT_MARGIN, window.innerWidth - windowWidth - VIEWPORT_MARGIN),
      y: clamp(y, getMinTop(), window.innerHeight - MIN_VISIBLE_HEIGHT),
    })
  }, [windowRef, width])

  const handlePointerDown = useCallback((e) => {
    if (e.button !== 0 || e.target.closest('button')) {
      return
    }
    dragOffsetRef.current = { x: e.clientX - position.x, y: e.clientY - position.y }
    e.currentTarget.setPointerCapture(e.pointerId)
  }, [position])

  const handlePointerMove = useCallback((e) => {
    if (!dragOffsetRef.current) {
      return
    }
    moveTo(e.clientX - dragOffsetRef.current.x, e.clientY - dragOffsetRef.current.y)
  }, [moveTo])

  const handlePointerUp = useCallback((e) => {
    if (!dragOffsetRef.current) {
      return
    }
    dragOffsetRef.current = null
    e.currentTarget.releasePointerCapture(e.pointerId)
  }, [])

  const handleKeyDown = useCallback((e) => {
    const delta = KEY_DELTAS[e.key]
    if (!delta) {
      return
    }
    e.preventDefault()
    moveTo(position.x + delta[0], position.y + delta[1])
  }, [position, moveTo])

  return {
    position,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleKeyDown,
  }
}

export default useDraggable
